// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import {
  BffInboundMessage,
  CoachMarkDismissAckEvent,
  CoachMarkErrorEvent,
  CoachMarkPreferencesAckEvent,
  CoachMarkResetAckEvent,
  CoachMarkShownAckEvent,
  CoachMarkStateEvent,
  CoachMarkStateItem,
  getSharedBffWebSocket,
} from '@/services/bffWebSocket';

const REQUEST_TIMEOUT_MS = 10_000;
const RETRY_DELAY_MS = 2_000;
const DUPLICATE_MARK_GUARD_MS = 1500;
const shownInFlight = new Set<string>();
const lastMarkedAtByKey = new Map<string, number>();

/**
 * Set to "1" in localStorage to suppress every coach tip for this browser. Automated UI runs
 * need it: a tip renders a full-screen modal overlay that swallows clicks on whatever it is
 * pointing at, and tips repeat until dismissed, so they reappear on every run.
 *
 * Read fresh rather than cached so a test can set it after the bundle has loaded. Null on
 * native, where React Native defines `window` but no `localStorage`.
 */
export const COACH_MARKS_DISABLED_STORAGE_KEY = 'goffy:disableCoachMarks';

const coachMarksSuppressed = (): boolean => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(COACH_MARKS_DISABLED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const toDefaultState = (key: string): CoachMarkStateItem => ({
  key,
  shownCount: 0,
  dismissed: false,
  shouldShow: true,
});

// Tips repeat until the user dismisses them, so the show count is informational only.
const normalizeState = (state: CoachMarkStateItem, enabled: boolean): CoachMarkStateItem => ({
  key: state.key,
  shownCount: Math.max(0, state.shownCount || 0),
  dismissed: !!state.dismissed,
  shouldShow: enabled && !state.dismissed,
});

const sanitizeKeys = (keys: readonly string[]): string[] =>
  Array.from(
    new Set(
      keys
        .map((key) => key?.trim())
        .filter((key): key is string => !!key && key.length > 0)
    )
  );

const buildRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `coach-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

type CoachMarkListener = () => void;

class CoachMarkClient {
  private readonly socket = getSharedBffWebSocket(this.apiBaseUrl);
  private readonly cache = new Map<string, CoachMarkStateItem>();
  private readonly listeners = new Set<CoachMarkListener>();
  private enabled = true;

  constructor(private readonly apiBaseUrl: string) {}

  subscribe(listener: CoachMarkListener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(key: string): CoachMarkStateItem | null {
    const normalized = key.trim();
    if (!normalized) return null;
    return this.cache.get(normalized) ?? null;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  async refresh(keys: readonly string[]): Promise<void> {
    const normalizedKeys = sanitizeKeys(keys);

    const requestId = buildRequestId();
    this.socket.sendCoachMarkStateRequest({ requestId, keys: normalizedKeys });
    const response = await this.waitForResponse<CoachMarkStateEvent>(requestId, 'coachMark.state');
    this.enabled = response.enabled !== false;
    const items = Array.isArray(response.items)
      ? response.items.map((item) => normalizeState(item, this.enabled))
      : [];
    const byKey = new Map(items.map((item) => [item.key, item] as const));
    for (const key of normalizedKeys) {
      this.cache.set(key, byKey.get(key) ?? this.applyEnabled(toDefaultState(key)));
    }
    this.notify();
  }

  async markShown(key: string): Promise<void> {
    const normalizedKey = key.trim();
    if (!normalizedKey) return;

    const requestId = buildRequestId();
    this.socket.sendCoachMarkShown({ requestId, key: normalizedKey });
    const response = await this.waitForResponse<CoachMarkShownAckEvent>(requestId, 'coachMark.shown.ack');
    this.cache.set(normalizedKey, normalizeState(response, this.enabled));
    this.notify();
  }

  async dismiss(key: string): Promise<void> {
    const normalizedKey = key.trim();
    if (!normalizedKey) return;

    const requestId = buildRequestId();
    this.socket.sendCoachMarkDismiss({ requestId, key: normalizedKey });
    const response = await this.waitForResponse<CoachMarkDismissAckEvent>(requestId, 'coachMark.dismiss.ack');
    this.cache.set(normalizedKey, normalizeState(response, this.enabled));
    this.notify();
  }

  async setEnabled(enabled: boolean): Promise<boolean> {
    const requestId = buildRequestId();
    this.socket.sendCoachMarkPreferences({ requestId, enabled });
    const response = await this.waitForResponse<CoachMarkPreferencesAckEvent>(
      requestId,
      'coachMark.preferences.ack'
    );
    this.enabled = response.enabled !== false;
    for (const [key, state] of this.cache.entries()) {
      this.cache.set(key, this.applyEnabled(state));
    }
    this.notify();
    return this.enabled;
  }

  async resetAll(): Promise<void> {
    const requestId = buildRequestId();
    this.socket.sendCoachMarkReset(requestId);
    const response = await this.waitForResponse<CoachMarkResetAckEvent>(requestId, 'coachMark.reset.ack');
    this.enabled = response.enabled !== false;
    for (const key of this.cache.keys()) {
      this.cache.set(key, this.applyEnabled(toDefaultState(key)));
    }
    this.notify();
  }

  private applyEnabled(state: CoachMarkStateItem): CoachMarkStateItem {
    return { ...state, shouldShow: this.enabled && !state.dismissed };
  }

  private notify() {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private waitForResponse<T extends BffInboundMessage>(requestId: string, successType: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      const unsubscribe = this.socket.onMessage((message) => {
        const typed = message as { type?: string; requestId?: string };
        if (typed.requestId !== requestId) return;
        if (typed.type === successType) {
          settled = true;
          cleanup();
          resolve(message as T);
          return;
        }
        if (typed.type === 'coachMark.error') {
          settled = true;
          cleanup();
          const errorMessage =
            (message as CoachMarkErrorEvent).message ??
            (message as CoachMarkErrorEvent).code ??
            'Coach mark request failed.';
          reject(new Error(errorMessage));
        }
      });

      const cleanup = () => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        unsubscribe();
      };

      timeoutHandle = setTimeout(() => {
        if (settled) return;
        cleanup();
        reject(new Error(`Timed out waiting for ${successType}.`));
      }, REQUEST_TIMEOUT_MS);
    });
  }
}

const clients = new Map<string, CoachMarkClient>();

const getCoachMarkClient = (apiBaseUrl: string): CoachMarkClient => {
  const existing = clients.get(apiBaseUrl);
  if (existing) return existing;
  const next = new CoachMarkClient(apiBaseUrl);
  clients.set(apiBaseUrl, next);
  return next;
};

export const tournamentsCreateCoachMarkKey = 'tournaments.createButton';
export const profileImageActionsCoachMarkKey = 'profile.imageActions';
export const holeCompactScoreCoachMarkKey = 'hole.compactScoreButton';
export const knownCoachMarkKeys = [
  tournamentsCreateCoachMarkKey,
  profileImageActionsCoachMarkKey,
  holeCompactScoreCoachMarkKey,
] as const;

type UseCoachMarkResult = {
  visible: boolean;
  shownCount: number;
  dismissed: boolean;
  loading: boolean;
  markShown: () => Promise<void>;
  /** Retires this one tip for good. */
  dismissForever: () => Promise<void>;
  /** Turns every coach tip off for this user. */
  disableAll: () => Promise<void>;
  refresh: () => Promise<void>;
};

export const useCoachMark = (
  apiBaseUrl: string,
  key: string,
  callerEnabled = true
): UseCoachMarkResult => {
  // Suppression gates the whole hook, not just `visible`, so a disabled run also skips the
  // state request and the shown-count write.
  const enabled = callerEnabled && !coachMarksSuppressed();
  const normalizedKey = key.trim();
  const trackingKey = `${apiBaseUrl}::${normalizedKey}`;
  const client = useMemo(() => getCoachMarkClient(apiBaseUrl), [apiBaseUrl]);
  const [state, setState] = useState<CoachMarkStateItem | null>(() => client.getState(normalizedKey));
  const [loading, setLoading] = useState<boolean>(enabled);
  const [hasError, setHasError] = useState(false);
  const markedForThisMountRef = useRef(false);

  useEffect(() => {
    const unsubscribe = client.subscribe(() => {
      setState(client.getState(normalizedKey));
    });
    return unsubscribe;
  }, [client, normalizedKey]);

  const refresh = useCallback(async () => {
    if (!enabled || !normalizedKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);
    try {
      await client.refresh([normalizedKey]);
    } catch (error) {
      console.warn('coach mark state refresh failed', { key: normalizedKey, error });
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [client, enabled, normalizedKey]);

  const markShown = useCallback(async () => {
    if (!enabled || !normalizedKey) return;
    if (shownInFlight.has(trackingKey)) return;
    const now = Date.now();
    const lastMarkedAt = lastMarkedAtByKey.get(trackingKey) ?? 0;
    if (now - lastMarkedAt < DUPLICATE_MARK_GUARD_MS) {
      markedForThisMountRef.current = true;
      return;
    }
    shownInFlight.add(trackingKey);
    setHasError(false);
    try {
      await client.markShown(normalizedKey);
      lastMarkedAtByKey.set(trackingKey, now);
      markedForThisMountRef.current = true;
    } catch (error) {
      console.warn('coach mark shown update failed', { key: normalizedKey, error });
      setHasError(true);
      markedForThisMountRef.current = false;
      throw error;
    } finally {
      shownInFlight.delete(trackingKey);
    }
  }, [client, enabled, normalizedKey, trackingKey]);

  const dismissForever = useCallback(async () => {
    if (!normalizedKey) return;
    try {
      await client.dismiss(normalizedKey);
    } catch (error) {
      console.warn('coach mark dismiss failed', { key: normalizedKey, error });
      throw error;
    }
  }, [client, normalizedKey]);

  const disableAll = useCallback(async () => {
    try {
      await client.setEnabled(false);
    } catch (error) {
      console.warn('coach mark disable-all failed', { key: normalizedKey, error });
      throw error;
    }
  }, [client, normalizedKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled || !hasError) return;
    const timer = setTimeout(() => {
      void refresh();
    }, RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled, hasError, refresh]);

  useEffect(() => {
    if (!enabled || !state || state.shownCount === 0) {
      markedForThisMountRef.current = false;
    }
  }, [enabled, normalizedKey, state?.shownCount, state]);

  useEffect(() => {
    if (!enabled || !normalizedKey || loading || hasError) return;
    if (!state?.shouldShow) return;
    if (markedForThisMountRef.current) return;

    markedForThisMountRef.current = true;
    void markShown().catch(() => {
      // Error already handled in markShown().
    });
  }, [enabled, hasError, loading, markShown, normalizedKey, state, trackingKey]);

  return {
    visible: enabled && !loading && !hasError && !!state?.shouldShow,
    shownCount: state?.shownCount ?? 0,
    dismissed: !!state?.dismissed,
    loading,
    markShown,
    dismissForever,
    disableAll,
    refresh,
  };
};

type UseCoachMarkPreferencesResult = {
  enabled: boolean;
  loading: boolean;
  error: boolean;
  saving: boolean;
  /** True when at least one individual tip is dismissed and can be brought back. */
  hasDismissedTips: boolean;
  setEnabled: (next: boolean) => Promise<void>;
  /** Re-enables coach tips and clears every individual dismissal. */
  resetAll: () => Promise<void>;
};

/**
 * Reads and writes the user-level coach tip preference, for the profile screen.
 */
export const useCoachMarkPreferences = (
  apiBaseUrl: string,
  keys: readonly string[] = knownCoachMarkKeys
): UseCoachMarkPreferencesResult => {
  const client = useMemo(() => getCoachMarkClient(apiBaseUrl), [apiBaseUrl]);
  const watchedKeys = useMemo(() => sanitizeKeys(keys), [keys]);
  const watchedKeysToken = watchedKeys.join('|');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  const subscribe = useCallback((listener: () => void) => client.subscribe(listener), [client]);
  const enabled = useSyncExternalStore(
    subscribe,
    () => client.isEnabled(),
    () => true
  );
  const hasDismissedTips = useSyncExternalStore(
    subscribe,
    () => watchedKeys.some((key) => client.getState(key)?.dismissed === true),
    () => false
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      await client.refresh(watchedKeys);
    } catch (refreshError) {
      console.warn('coach mark preferences refresh failed', refreshError);
      setError(true);
    } finally {
      setLoading(false);
    }
    // watchedKeysToken keeps the identity of the key list stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, watchedKeysToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setEnabled = useCallback(
    async (next: boolean) => {
      setSaving(true);
      setError(false);
      try {
        await client.setEnabled(next);
      } catch (setError_) {
        console.warn('coach mark preference update failed', setError_);
        setError(true);
        throw setError_;
      } finally {
        setSaving(false);
      }
    },
    [client]
  );

  const resetAll = useCallback(async () => {
    setSaving(true);
    setError(false);
    try {
      await client.resetAll();
      lastMarkedAtByKey.clear();
      await client.refresh(watchedKeys);
    } catch (resetError) {
      console.warn('coach mark reset failed', resetError);
      setError(true);
      throw resetError;
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, watchedKeysToken]);

  return { enabled, loading, error, saving, hasDismissedTips, setEnabled, resetAll };
};

export const resetCoachMarksForDebug = async (apiBaseUrl: string): Promise<void> => {
  await getCoachMarkClient(apiBaseUrl).resetAll();
  shownInFlight.clear();
  lastMarkedAtByKey.clear();
};

export const getCoachMarkStatesForDebug = async (
  apiBaseUrl: string,
  keys: readonly string[] = knownCoachMarkKeys
): Promise<CoachMarkStateItem[]> => {
  const normalizedKeys = sanitizeKeys(keys);
  if (normalizedKeys.length === 0) return [];

  const client = getCoachMarkClient(apiBaseUrl);
  await client.refresh(normalizedKeys);
  return normalizedKeys.map((key) => client.getState(key) ?? toDefaultState(key));
};
